import React, {useContext, useEffect, useRef, useState} from "react";
import {FetchContext} from "../../context/FetchContext";
import {AuthContext} from "../../context/AuthContext";
import {
    formatToReadableLargeTitle,
    getFileTypeFromMime,
    IMAGE_UPLOAD, LibrettoProjectTypes, LibrettoWorkflowTypes,
    RefreshTokenAndRetry,
    UploadToLibretto
} from "../../utils/utils";
import {collection, doc, onSnapshot, query, updateDoc, where} from "firebase/firestore";
import {db} from "../../Firebase";
import {
    kAssetsCollectionName,
    kLibrettoUserCollectionName,
    kProjectsCollectionName,
    LibrettoUserId
} from "../../utils/CollectionConstants";
import mixpanel from "mixpanel-browser";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {AudioWaveformIcon} from "./ProjectThumbnail";
import {Typography} from "@mui/material";
import NewTranscriptionLangDropdown from "../NewStudio/NewTranscriptionLangDropdown";
import {useNavigate} from "react-router-dom";
import {RadioButtonCheckedOutlined} from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";
import ScheduleSessionForm from "./CreateSession";
import {useAssetStore} from "../../storage/AssetStore";


const ContentLanguageArea = ({selectedLanguage, updateUserLanguageCode, compact = false}) => {
    const containerStyle = compact ? {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: "8px",
        borderRadius: "12px",
        border: "1px solid var(--app-border-subtle)",
        backgroundColor: "var(--app-bg-surface)",
        padding: "14px",
    } : {
        flexDirection: "column",
        display: "flex",
        width: "660px",
        gap: "8px",
    };

    const titleStyle = compact ? {
        fontFamily: "Inter",
        fontSize: "13px",
        fontWeight: 600,
        color: "var(--app-text-primary)",
        lineHeight: "18px",
        marginBottom: "4px",
    } : {
        fontFamily: "Inter",
        fontSize: "18px",
        fontWeight: 600,
        color: "#333333",
        marginBottom: "4px",
    };

    const subtitleStyle = compact ? {
        fontFamily: "Inter",
        fontSize: "12px",
        fontWeight: 400,
        color: "var(--app-text-muted)",
        lineHeight: "18px",
    } : {
        fontFamily: "Inter",
        fontSize: "13.5px",
        fontWeight: 500,
        color: "#999999",
        lineHeight: "1.4",
    };

    return (
        <Box
            style={containerStyle}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexDirection: compact ? "column" : "row",
                    gap: compact ? "8px" : "0px",
                }}
            >
                <div style={{maxWidth: compact ? "100%" : "500px"}}>
                    <div
                        style={titleStyle}
                    >
                        Content Language
                    </div>
                    <div
                        style={subtitleStyle}
                    >
                        If you know it, set the language of speech content for better transcription.
                    </div>
                </div>
                <Box
                    style={{paddingBottom: compact ? "0px" : "10px"}}
                    className="dropdown-class"
                    onClick={(e) => e.stopPropagation()}
                >
                    <NewTranscriptionLangDropdown
                        selectedLanguage={selectedLanguage}
                        onChange={updateUserLanguageCode}
                    />
                </Box>
            </div>
        </Box>
    )
}

const FileExceedsLimitsMessage = ({ message }) => {
    return (
        <Box
            style={{
                flexDirection: "column",
                display: "flex",
                width: "660px",
                gap: "8px",
                padding: "16px",
                backgroundColor: "#FFF3CD",
                borderRadius: "8px",
                border: "1px solid #FFE6A6",
            }}
        >
            <div
                style={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#856404",
                    marginBottom: "4px",
                }}
            >
                File Size/Duration Exceeded
            </div>
            <div
                style={{
                    fontFamily: "Inter",
                    fontSize: "13.5px",
                    fontWeight: 500,
                    color: "#856404",
                    lineHeight: "1.4",
                }}
            >
                {message}
            </div>
        </Box>
    )
}

const FileUploadPreview = ({file, compact = false}) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const [aspectRatio, setAspectRatio] = useState('horizontal');

    useEffect(() => {
        if (file && file.type.startsWith('video/')) {
            const videoUrl = URL.createObjectURL(file);
            const video = document.createElement('video');
            video.src = videoUrl;
            video.onloadedmetadata = () => {
                const ratio = video.videoWidth / video.videoHeight;
                if (ratio > 1.1) setAspectRatio('horizontal');
                else if (ratio < 0.9) setAspectRatio('vertical');
                else setAspectRatio('square');

                video.currentTime = 1; // Seek to 1 second
            };
            video.onseeked = () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                setPreviewUrl(canvas.toDataURL());
                URL.revokeObjectURL(videoUrl); // Clean up the object URL
            };
        }

        // Cleanup function
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [file]);

    const isVideo = file && file.type.startsWith('video/');
    const isAudio = file && file.type.startsWith('audio/');
    const isImage = file && file.type.startsWith('image/');

    const uploadAreaStyleSecond = compact ? {
        alignSelf: "stretch",
        height: "300px",
        borderRadius: "14px",
        border: "1px solid var(--app-border-subtle)",
        backgroundColor: "var(--app-bg-subtle)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
    } : {
        alignSelf: 'stretch',
        height: "390px",
        padding: '24px',
        borderRadius: '6px',
        border: '1px #BFBFBF dotted',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
    };

    const thumbnailContainerStyle = compact ? {
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "var(--app-bg-subtle)",
    } : {
        position: 'relative',
        width: '647px',
        height: '390px',
        borderRadius: '6px',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f0f0f0',
    };

    const getThumbnailStyle = () => {
        const baseStyle = {
            objectFit: 'contain',
            maxWidth: '100%',
            maxHeight: '100%',
        };

        switch (aspectRatio) {
            case 'vertical':
                return { ...baseStyle, height: '100%', width: 'auto' };
            case 'square':
                return { ...baseStyle, height: '100%', width: 'auto' };
            case 'horizontal':
            default:
                return { ...baseStyle, width: '100%', height: 'auto' };
        }
    };

    const overlayStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isHovered ? 1 : 0,
        transition: 'opacity 0.3s ease',
    };

    const titleStyle = {
        color: 'white',
        textAlign: 'center',
        padding: '10px',
    };

    return (
        <Box sx={uploadAreaStyleSecond}>
            <Box
                sx={thumbnailContainerStyle}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {isVideo && previewUrl && (
                    <img src={previewUrl} alt="Video thumbnail" style={getThumbnailStyle()} />
                )}
                {isAudio && (
                    <AudioWaveformIcon style={getThumbnailStyle()} />
                )}
                {isImage && (
                    <img src={URL.createObjectURL(file)} alt="Image preview" style={getThumbnailStyle()} />
                )}
                <Box sx={overlayStyle}>
                    <Typography sx={titleStyle}>
                        {formatToReadableLargeTitle(file.name)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const WorkflowTypeToTitle = (workflowType) => {
    if (workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO) {
        return "Upload and start editing";
    }
    if (workflowType === LibrettoWorkflowTypes.EDIT_FOR_CLARITY) {
        return "Edit for clarity";
    }
    if (workflowType === LibrettoWorkflowTypes.TRANSCRIBE_FILE) {
        return "Transcribe a file";
    }
    if (workflowType === LibrettoWorkflowTypes.ADD_CAPTIONS) {
        return "Add captions";
    }
    if (workflowType === LibrettoWorkflowTypes.MAKE_PODCAST) {
        return "Make a podcast";
    }
    if (workflowType === LibrettoWorkflowTypes.CLEAN_UP_AUDIO) {
        return "Clean up audio";
    }
    if (workflowType === LibrettoWorkflowTypes.SCHEDULE_RECORDING) {
        return "Schedule a recording session";
    }
    if (workflowType === LibrettoWorkflowTypes.CREATE_SOCIAL_CLIPS) {
        return "Create social clips";
    }
    if (workflowType === LibrettoWorkflowTypes.TRANSLATE_AND_DUB) {
        return "Translate & dub video";
    }
}

const WorkflowTypeToSubtitle = (workflowType) => {
    if (workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO) {
        return "Transcribes file and loads a composition so you can edit by text or on the timeline.";
    }
    if (workflowType === LibrettoWorkflowTypes.TRANSCRIBE_FILE) {
        return "Upload a file or start recording to transcribe it.";
    }
    if (workflowType === LibrettoWorkflowTypes.EDIT_FOR_CLARITY) {
        return "Upload a file to remove filler words, tighten gaps, remove retakes, and enhance audio."
    }
    if (workflowType === LibrettoWorkflowTypes.ADD_CAPTIONS) {
        return "Upload a file with audio and apply captions with one click.";
    }
    if (workflowType === LibrettoWorkflowTypes.MAKE_PODCAST) {
        return "Start recording or upload a file to create a podcast.";
    }
    if (workflowType === LibrettoWorkflowTypes.CLEAN_UP_AUDIO) {
        return "Upload a file to apply Studio Sound and remove background noise.";
    }
    if (workflowType === LibrettoWorkflowTypes.SCHEDULE_RECORDING) {
        return "Schedule a recording session and optionally invite others to join.";
    }
    if (workflowType === LibrettoWorkflowTypes.CREATE_SOCIAL_CLIPS) {
        return "Upload a file to get clips for social media.";
    }
    if (workflowType === LibrettoWorkflowTypes.TRANSLATE_AND_DUB) {
        return "Upload a file with audio to use an AI voice to narrate in over 20 languages.";
    }
}

const WorkflowTypeToProjectType = (workflowType) => {
    if (workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO || workflowType === LibrettoWorkflowTypes.ADD_CAPTIONS) {
        return LibrettoProjectTypes.ProjectTypeEditor;
    }
    return LibrettoProjectTypes.ProjectTypeEditor;
}

const AgenticUploadContentCard = ({onClose, projectId, workflowType, initialFile = null, onUploadInitiated}) => {
    const fetchContext = useContext(FetchContext);
    const authContext = useContext(AuthContext);

    const userId = authContext.getUserId();

    const [uploadFileChosen, setUploadFileChosen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [chosenFileType, setChosenFileType] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [selectedTranscriptionMode, setSelectedTranscriptionMode] = useState("word-accuracy");
    const uploadInputRef = useRef(null);
    const [fileExceedsLimits, setFileExceedsLimits] = useState(false);
    const [limitExceededMessage, setLimitExceededMessage] = useState("");

    const showRecordOptions = workflowType === LibrettoWorkflowTypes.MAKE_PODCAST;
    const isUploadAndEditFlow = workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO;

    const showRecordToTranscribe = workflowType === LibrettoWorkflowTypes.TRANSCRIBE_FILE;

    const navigate = useNavigate();

    const { UploadAsset } = useAssetStore();

    useEffect(() => {
        // Reset state when component mounts or projectId changes
        setUploadFileChosen(false);
        setSelectedFile(null);
        setChosenFileType(null);
        setFileExceedsLimits(false);
        setLimitExceededMessage("");
        if (uploadInputRef.current) {
            uploadInputRef.current.value = '';
        }
    }, [projectId]);

    const handleFileSelected = async (file) => {
        setSelectedFile(file);
        setUploadFileChosen(true);
        const mimeType = getFileTypeFromMime(file.type);
        setChosenFileType(mimeType);

        const fileSizeGB = file.size / (1024 * 1024 * 1024); // Convert to GB
        if (fileSizeGB > 10) {
            setFileExceedsLimits(true);
            setLimitExceededMessage("File size exceeds 10GB limit. Please use a smaller file.");
            return;
        }

        if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
            const url = URL.createObjectURL(file);
            const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
            media.src = url;
            media.onloadedmetadata = () => {
                const durationInHours = media.duration / 3600;
                if (durationInHours > 6) {
                    setFileExceedsLimits(true);
                    setLimitExceededMessage("File duration exceeds 6 hour limit. Please use a shorter file.");
                } else {
                    setFileExceedsLimits(false);
                    setLimitExceededMessage("");
                }
                URL.revokeObjectURL(url);
            };
            media.onerror = () => {
                console.error("Error loading media file");
                URL.revokeObjectURL(url);
                setFileExceedsLimits(false);
                setLimitExceededMessage("");
            };
        } else {
            setFileExceedsLimits(false);
            setLimitExceededMessage("");
        }
    }

    const startUpload = async (input) => {
        uploadInputRef.current = input;
        if (input?.files?.[0]) {
            await handleFileSelected(input.files[0]);
        }
    }

    useEffect(() => {
        if (initialFile && !uploadFileChosen) {
            void handleFileSelected(initialFile);
        }
    }, [initialFile, uploadFileChosen]);

    useEffect(() => {

        if (!userId) {
            return;
        }

        const q = query(collection(db, kLibrettoUserCollectionName), where(LibrettoUserId, "==", userId));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const newUserState = [];
            querySnapshot.forEach((doc) => {
                newUserState.push(doc.data());
            });
            if (newUserState.length > 0) {
                const defaultLanguageForWorkflow = workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO ? "auto" : "en";
                setSelectedLanguage(newUserState[0].languageCode ? newUserState[0].languageCode : defaultLanguageForWorkflow);
                setSelectedTranscriptionMode(newUserState[0].transcriptionMode ? newUserState[0].transcriptionMode : "word-accuracy");
            }
        });

        return () => unsubscribe();
    }, [userId, setSelectedLanguage, workflowType]);

    const setProgress = async ({assetId, uploadProgress}) => {
        const entityRef = doc(db, kAssetsCollectionName, assetId);
        try {
            await updateDoc(entityRef, {uploadProgress: uploadProgress});
        } catch (e) {
            console.error('Error updating upload progress for asset: ', e)
        }
    }

    const setProjectProgress = async ({projectId, uploadProgress}) => {
        const entityRef = doc(db, kProjectsCollectionName, projectId);
        try {
            await updateDoc(entityRef, {uploadProgress: uploadProgress});
        } catch (e) {
            console.error('Error updating upload progress for asset: ', e)
        }
    }

    const headerStyle = {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    }

    const headerTextStyle = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '8px',
    }

    const titleStyle = {
        color: '#1A1A1A',
        fontSize: '18px',
        fontFamily: 'Inter',
        fontWeight: '500',
        lineHeight: '24px',
        letterSpacing: '0.18px',
    }

    const subtitleStyle = {
        width: isUploadAndEditFlow ? "100%" : '508px',
        color: isUploadAndEditFlow ? "var(--app-text-muted)" : '#999999',
        fontSize: '14px',
        fontFamily: 'Inter',
        fontWeight: '400',
        lineHeight: '20px',
        letterSpacing: '0.14px',
    }

    const uploadAreaStyleSecond = {
        alignSelf: 'stretch',
        height: '228px',
        padding: '24px',
        borderRadius: '6px',
        border: '1px #BFBFBF dotted',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
    }

    const uploadButtonStyle = isUploadAndEditFlow ? {
        width: "100%",
        height: "52px",
        borderRadius: "999px",
        backgroundColor: "#1A1A1A",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: "#111827",
        },
    } : {
        alignSelf: 'stretch',
        padding: '14px 20px',
        background: '#2B6BFD',
        borderRadius: '8px',
        marginTop: "-8px",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        // Set hovered state
        "&:hover": {
            background: "linear-gradient(0deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%), #2B6BFD",
        },
        // Set focused state
        "&:focus": {
            background: "linear-gradient(0deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), #2B6BFD"
        },
    }

    const disabledUploadButtonStyle = {
        ...uploadButtonStyle,
        background: isUploadAndEditFlow ? "#9CA3AF" : '#E0E0E0',
        cursor: 'not-allowed',
        opacity: 0.75,
        "&:hover": {
            background: isUploadAndEditFlow ? "#9CA3AF" : "#E0E0E0",
        },
        "&:focus": {
            background: isUploadAndEditFlow ? "#9CA3AF" : "#E0E0E0"
        },
    }

    const uploadButtonTextStyle = {
        color: 'white',
        fontSize: '14px',
        fontFamily: 'Inter',
        fontWeight: '500',
        lineHeight: '20px',
        letterSpacing: '0.14px',
    }

    const handleUpload = async () => {
        if (fileExceedsLimits) {
            return;
        }
        
        onClose();

        const projectType = WorkflowTypeToProjectType(workflowType);

        try {
            // UploadToLibretto
            const assetId = await UploadAsset({
                projectId: projectId,
                inputRef: uploadInputRef.current,
                file: selectedFile,
                projectType: projectType,
                languageCode: selectedLanguage,
                transcriptionMode: selectedTranscriptionMode,
                filetype: getFileTypeFromMime((selectedFile || uploadInputRef.current.files[0]).type),
                onUploadInitiated: ({assetId: nextAssetId, projectId: nextProjectId}) => {
                    if (onUploadInitiated) {
                        onUploadInitiated({
                            assetId: nextAssetId,
                            projectId: nextProjectId,
                            languageCode: selectedLanguage,
                        });
                    }
                },
            });
            mixpanel.track('Uploaded asset', {
                'filetype': getFileTypeFromMime((selectedFile || uploadInputRef.current.files[0]).type),
                'filesize': (selectedFile || uploadInputRef.current.files[0]).size,
                'assetId': assetId,
                'languageCode': selectedLanguage,
                'transcriptionMode': selectedTranscriptionMode,
            });
        } catch (e) {
            console.log("Error is: ", e)
            if (e.response && e.response.status === 401) {
                await RefreshTokenAndRetry(e, authContext, fetchContext);
            }
        }
    }

    const initialCardStyle = {
        position: 'fixed',
        top: 'calc(50% - 360px)',
        left: 'calc(50% - 320px)',
        display: 'flex',
        width: '715px',
        padding: isUploadAndEditFlow ? '24px' : '24px 24px 40px 24px',
        flexDirection: 'column',
        alignItems: isUploadAndEditFlow ? 'stretch' : 'center',
        gap: isUploadAndEditFlow ? '20px' : '32px',
        borderRadius: '12px',
        zIndex: 1000,
        background: isUploadAndEditFlow ? "var(--app-bg-surface)" : "#FFFFFF",
        border: isUploadAndEditFlow ? "1px solid var(--app-border-subtle)" : "none",
    }

    const higherInitialCardStyle = {
        ...initialCardStyle,
        top: 'calc(50% - 400px)',
    }

    const headerAreaStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
    }

    const headerTextAreaStyle = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: isUploadAndEditFlow ? "4px" : '2px',
    }

    const headerText = isUploadAndEditFlow ? {
        color: "var(--app-text-primary)",
        fontFamily: "Inter",
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: "28px",
        letterSpacing: "-0.01em",
    } : {
        fontFamily: 'Inter',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: '0.18px',
    }

    const baseTextStyle = {
        fontFamily: 'Inter',
        fontSize: 16,
        fontStyle: 'bold',
        fontWeight: 600,
        lineHeight: '20px',
        letterSpacing: '0.14px',
    }

    const subtextStyle = {
        ...baseTextStyle,
        color: '#999',
    }

    const innerTextStyle = {
        ...baseTextStyle,
        color: isUploadAndEditFlow ? "var(--app-text-primary)" : '#1a1a1a',
    }

    const uploadAreaStyle = isUploadAndEditFlow ? {
        display: 'flex',
        height: "300px",
        flexDirection: 'column',
        padding: "24px",
        justifyContent: 'center',
        alignItems: 'center',
        gap: "12px",
        alignSelf: 'stretch',
        borderRadius: "14px",
        border: "1px dashed var(--app-border-muted)",
        backgroundColor: "var(--app-bg-canvas)",
        cursor: "pointer",
        ':hover': {
            backgroundColor: "var(--app-bg-subtle)",
            borderColor: "var(--app-border-muted)",
        }
    } : {
        display: 'flex',
        height: "390px",
        flexDirection: 'column',
        padding: "24px",
        justifyContent: 'center',
        alignItems: 'center',
        gap: "12px",
        alignSelf: 'stretch',
        borderRadius: "6px",
        border: "1px dashed #BFBFBF",
        cursor: "pointer",
        // Hover styles
        ':hover': {
            backgroundColor: "#F5F5F5",
            borderColor: "#999999",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)"
        }
    }

    const smallActionAreaStyle = {
        display: 'flex',
        flexDirection: 'row',
        width: "328px",
        gap: "8px",
        height: "64px",
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderRadius: "6px",
        border: "0.5px solid #BFBFBF",
        cursor: "pointer",
        // Hover styles
        ':hover': {
            backgroundColor: "#F5F5F5",
            borderColor: "#999999",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)"
        }
    }

    const largeActionAreaStyle = {
        display: 'flex',
        flexDirection: 'row',
        width: "667px",
        gap: "8px",
        height: "64px",
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderRadius: "6px",
        border: "0.5px solid #BFBFBF",
        cursor: "pointer",
        // Hover styles
        ':hover': {
            backgroundColor: "#F5F5F5",
            borderColor: "#999999",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)"
        }
    }

    const iconStyle = {
        display: 'flex',
        width: "20px",
        height: "20px",
        justifyContent: 'center',
        alignItems: 'center',
    }

    const updateUserLanguageCode = async (languageCode) => {

        const entityRef = doc(db, kLibrettoUserCollectionName, userId);
        try {
            await updateDoc(entityRef, {languageCode: languageCode});
        } catch (error) {
            console.error('Error updating recording type for room: ', error);
        }
    }

    const acceptStr = workflowType === LibrettoWorkflowTypes.EDIT_AUDIO_VIDEO
        ? "video/*, audio/*"
        : "video/*, audio/*, image/*";

    const navigateToCameraRecord = () => {
        navigate("/editor?mode=recording&type=video")
    }

    const navigateToStudio = () => {
        navigate("/studio")
    }

    if (workflowType === LibrettoWorkflowTypes.SCHEDULE_RECORDING) {
        return (
            <Box sx={higherInitialCardStyle}>
                <Box sx={headerAreaStyle}>
                    <Box sx={headerTextAreaStyle}>
                        <Box sx={headerText}>
                            {WorkflowTypeToTitle(workflowType)}
                        </Box>
                        <Box sx={subtitleStyle}>
                            {WorkflowTypeToSubtitle(workflowType)}
                        </Box>
                    </Box>
                    <Box sx={{cursor: "pointer"}} onClick={onClose}>
                        <CloseIcon/>
                    </Box>
                </Box>
                <ScheduleSessionForm onClose={onClose} projectId={projectId}/>
            </Box>
        );
    }

    if (uploadFileChosen) {
        return (
            <Box sx={initialCardStyle}>
                <Box sx={headerAreaStyle}>
                    <Box sx={headerTextAreaStyle}>
                        <Box sx={headerText}>
                            {WorkflowTypeToTitle(workflowType)}
                        </Box>
                        <Box sx={subtitleStyle}>
                            {WorkflowTypeToSubtitle(workflowType)}
                        </Box>
                    </Box>
                    <Box
                        sx={isUploadAndEditFlow ? {
                            width: "32px",
                            height: "32px",
                            borderRadius: "10px",
                            border: "1px solid var(--app-border-subtle)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "var(--app-text-muted)",
                            "&:hover": {
                                backgroundColor: "var(--app-bg-subtle)",
                                color: "var(--app-text-primary)",
                            },
                        } : {cursor: "pointer"}}
                        onClick={onClose}
                    >
                        <CloseIcon sx={isUploadAndEditFlow ? {fontSize: "20px"} : {}}/>
                    </Box>
                </Box>
                <FileUploadPreview file={selectedFile} compact={isUploadAndEditFlow}/>
                {fileExceedsLimits ? (
                    <FileExceedsLimitsMessage message={limitExceededMessage}/>
                ) : (
                    chosenFileType !== IMAGE_UPLOAD && (
                        <ContentLanguageArea
                            selectedLanguage={selectedLanguage}
                            updateUserLanguageCode={updateUserLanguageCode}
                            compact={isUploadAndEditFlow}
                        />
                    )
                )}
                <Box sx={fileExceedsLimits ? disabledUploadButtonStyle : uploadButtonStyle} onClick={handleUpload}>
                    <Box sx={uploadButtonTextStyle}>Upload</Box>
                </Box>
            </Box>
        );
    }


    return (
        <Box sx={initialCardStyle}>
            <Box sx={headerAreaStyle}>
                <Box sx={headerTextAreaStyle}>
                    <Box sx={headerText}>
                        {WorkflowTypeToTitle(workflowType)}
                    </Box>
                    <Box sx={subtitleStyle}>
                        {WorkflowTypeToSubtitle(workflowType)}
                    </Box>
                </Box>
                <Box
                    sx={isUploadAndEditFlow ? {
                        width: "32px",
                        height: "32px",
                        borderRadius: "10px",
                        border: "1px solid var(--app-border-subtle)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "var(--app-text-muted)",
                        "&:hover": {
                            backgroundColor: "var(--app-bg-subtle)",
                            color: "var(--app-text-primary)",
                        },
                    } : {cursor: "pointer"}}
                    onClick={onClose}
                >
                    <CloseIcon sx={isUploadAndEditFlow ? {fontSize: "20px"} : {}}/>
                </Box>
            </Box>
            <Box sx={uploadAreaStyle} htmlFor="media-upload" component="label">
                <Box sx={innerTextStyle}>
                    {"Upload video or audio"}
                </Box>
                <Box sx={{
                    color: isUploadAndEditFlow ? "var(--app-text-muted)" : "#999",
                    fontSize: "13px",
                    fontFamily: "Inter",
                    lineHeight: "18px",
                }}>
                    {"Click to browse or drag and drop your file."}
                </Box>
            </Box>
            <input hidden accept={acceptStr} type="file" id="media-upload"
                   key={`file-input-${projectId}`}
                   onChange={(e) => startUpload(e.target)}/>
            <Box sx={{display: showRecordOptions ? "flex" : "none", flexDirection: "row", alignItems: "space-between", gap: "12px"}}>
                <Box sx={smallActionAreaStyle} onClick={navigateToCameraRecord}>
                    <Box sx={{paddingTop: "7px"}}>
                        <RadioButtonCheckedOutlined sx={{color: "red"}}/>
                    </Box>
                    <Box sx={innerTextStyle}>
                        {"Record your camera or mic"}
                    </Box>
                </Box>
                <Box sx={smallActionAreaStyle} onClick={navigateToStudio}>
                    <Box sx={{paddingTop: "7px"}}>
                        <GroupIcon/>
                    </Box>
                    <Box sx={innerTextStyle}>
                        {"Record with others"}
                    </Box>
                </Box>
            </Box>
            <Box sx={{display: showRecordToTranscribe ? "flex" : "none", flexDirection: "row"}}>
                <Box sx={largeActionAreaStyle} onClick={navigateToCameraRecord}>
                    <Box sx={{paddingTop: "7px"}}>
                        <RadioButtonCheckedOutlined/>
                    </Box>
                    <Box sx={innerTextStyle}>
                        {"Record to start transcribing"}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AgenticUploadContentCard;
